import * as THREE from "three";

import { CSG } from "@enable3d/three-graphics/jsm/csg";
import { mergeBufferGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry";

import { COMPONENT_ALIGNMENT, DEFAULT_EMPTY_THICKNESS } from "../Constants";
import { align, fromMM } from "./frameUtils";

export const buildHollowFrame = (width, height, depth, thickness) => {
  const shape = new THREE.Shape();
  let holePath = new THREE.Path();
  let extrudeSettings = {
    depth: width,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };

  const offsetWidth = depth / 2;
  const offsetHeight = height / 2;

  shape
    .moveTo(0 - offsetWidth, 0 - offsetHeight)
    .lineTo(0 - offsetWidth, height - offsetHeight)
    .lineTo(depth - offsetWidth, height - offsetHeight)
    .lineTo(depth - offsetWidth, 0 - offsetHeight)
    .lineTo(0 - offsetWidth, 0 - offsetHeight);

  holePath
    .moveTo(thickness - offsetWidth, thickness - offsetHeight)
    .lineTo(thickness - offsetWidth, height - thickness - offsetHeight)
    .lineTo(depth - offsetWidth - thickness, height - thickness - offsetHeight)
    .lineTo(depth - offsetWidth - thickness, thickness - offsetHeight)
    .lineTo(thickness - offsetWidth, thickness - offsetHeight);

  shape.holes.push(holePath);

  let extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  extrudeGeometry.applyMatrix4(new THREE.Matrix4().makeRotationY(Math.PI / 2));
  extrudeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(-width / 2, 0, 0),
  );

  return extrudeGeometry;
};

export const buildPipe = (radius, thickness, length) => {
  const shape = new THREE.Shape();
  var holePath = new THREE.Path();

  shape.absarc(0, 0, radius, 0, Math.PI * 2, 0, false);
  holePath.absarc(0, 0, radius - thickness, 0, Math.PI * 2, true);

  shape.holes.push(holePath);

  let extrudeSettings = {
    depth: length,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 6,
  };
  let extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

  return extrudeGeometry;
};

function createBentDuctShape(xRadius, yRadius, width, height, thickness) {
  const extendDown = height - yRadius;
  const extentLeft = width - xRadius;

  var curve = new THREE.EllipseCurve(
    0,
    0,
    xRadius,
    yRadius,
    0,
    Math.PI / 2,
    false,
  );

  var points = curve.getSpacedPoints(20);

  points.splice(
    0,
    0,
    new THREE.Vector2(width - extentLeft, -extendDown - thickness),
  ); // Starting point

  //
  points.push(new THREE.Vector2(-extentLeft - thickness, height - extendDown));
  points.push(new THREE.Vector2(-extentLeft - thickness, 0));

  // corner point
  points.push(new THREE.Vector2(0, 0));

  // Finish point to join first added point
  points.push(new THREE.Vector2(0, -extendDown - thickness));

  const shape = new THREE.Shape();
  shape.setFromPoints(points);

  return shape;
}

// export function buildEspPipe(
//   width,
//   height,
//   length,
//   tubeThickness,
//   distanceToFrame,
//   intrudingLength,
//   downwardSlope = 0,
// ) {
//   width -= tubeThickness * 2;
//   //height -= tubeThickness * 2;

//   const roundedHeight = 0.05;

//   function ccc(t) {
//     const t2 = ((2 * Math.PI) / 2) * t * 0.5;
//     const x = -Math.cos(t2);
//     const z =  -Math.sin(t2);

//     const y = t;
//    return new THREE.Vector3(x * width, y * -roundedHeight, z * (length / 2));
//    // return new THREE.Vector3(-x * width, 0, 0);
//   }

//   let curve = [];
//   let numberOfPoints = 30;

//   for (let i = 0; i <= numberOfPoints; i++) {
//     curve.push(ccc(i / numberOfPoints));
//   }

//   const uplift = 0.0;
//   const gap = width / 2;

//   console.log("curve", curve);

//   curve.unshift(
//     curve[0]
//       .clone()
//       .set(curve[0].x, curve[0].y + uplift, curve[0].z + gap * 0.4)
//   );
//   // curve.unshift(
//   //   curve[0]
//   //     .clone()
//   //     .set(curve[0].x, curve[0].y - downwardSlope, curve[0].z + gap * 0.6)
//   // );
//   // curve.unshift(
//   //   curve[0]
//   //     .clone()
//   //     .set(curve[0].x, curve[0].y, curve[0].z + (distanceToFrame - width / 2))
//   // );
//   // curve.unshift(
//   //   curve[0].clone().set(curve[0].x-0.2, curve[0].y, curve[0].z + intrudingLength)
//   // );

//   let lastPoint = curve[curve.length - 1].clone();

//   // curve.push(
//   //   lastPoint.set(
//   //     lastPoint.x,
//   //     lastPoint.y - (height - roundedHeight),
//   //     lastPoint.z,
//   //   ),
//   // );

//   console.log("finished - curve", curve);

//   const spline = new THREE.CatmullRomCurve3(curve);

//   let tubeGeometry = new THREE.TubeGeometry(
//     spline,
//     32,
//     tubeThickness,
//     10,
//     false,
//   );
//   tubeGeometry.applyMatrix4(
//     new THREE.Matrix4().makeTranslation(width / 2, height / 2, 0),
//   );

//   return tubeGeometry;
// }

export function buildOdourPipe(
  width,
  height,
  tubeThickness,
  distanceToFrame,
  intrudingLength,
  downwardSlope,
) {
  width -= tubeThickness * 2;
  //height -= tubeThickness * 2;

  const roundedHeight = 0.05;

  function ccc(t) {
    const t2 = 2 * Math.PI * t * 0.5;
    const x = -Math.cos(t2);
    const z = -Math.sin(t2);
    const y = t;
    return new THREE.Vector3(
      x * (width / 2),
      y * -roundedHeight,
      z * (width / 2),
    );
  }

  let curve = [];
  let numberOfPoints = 30;

  for (let i = 0; i <= numberOfPoints; i++) {
    curve.push(ccc(i / numberOfPoints));
  }

  const uplift = 0.0;
  const gap = width / 2;

  curve.unshift(
    curve[0]
      .clone()
      .set(curve[0].x, curve[0].y + uplift, curve[0].z + gap * 0.4),
  );
  curve.unshift(
    curve[0]
      .clone()
      .set(curve[0].x, curve[0].y - downwardSlope, curve[0].z + gap * 0.6),
  );
  curve.unshift(
    curve[0]
      .clone()
      .set(curve[0].x, curve[0].y, curve[0].z + (distanceToFrame - width / 2)),
  );
  curve.unshift(
    curve[0].clone().set(curve[0].x, curve[0].y, curve[0].z + intrudingLength),
  );

  let lastPoint = curve[curve.length - 1].clone();
  curve.push(
    lastPoint.set(
      lastPoint.x,
      lastPoint.y - (height - roundedHeight),
      lastPoint.z,
    ),
  );

  const spline = new THREE.CatmullRomCurve3(curve);

  let tubeGeometry = new THREE.TubeGeometry(
    spline,
    32,
    tubeThickness,
    10,
    false,
  );
  tubeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(width / 2, height / 2, 0),
  );

  return tubeGeometry;
}

export function buildOzonePipe(
  width,
  height,
  tubeThickness,
  distanceToFrame,
  intrudingLength,
) {
  width -= tubeThickness * 2;
  height -= tubeThickness * 2;

  function ccc(t) {
    const t2 = 2 * Math.PI * t * 0.75;
    const x = -Math.cos(t2);
    const z = -Math.sin(t2);
    const y = t;
    return new THREE.Vector3(x * (width / 2), y * -height, z * (width / 2));
  }

  let curve = [];
  let numberOfPoints = 30;

  for (let i = 0; i <= numberOfPoints; i++) {
    curve.push(ccc(i / numberOfPoints));
  }

  const uplift = 0.015;
  const downwardSlope = 0.045;
  const gap = width / 2;

  curve.unshift(
    curve[0]
      .clone()
      .set(curve[0].x, curve[0].y + uplift, curve[0].z + gap * 0.4),
  );
  curve.unshift(
    curve[0]
      .clone()
      .set(curve[0].x, curve[0].y - downwardSlope, curve[0].z + gap * 0.6),
  );
  curve.unshift(
    curve[0].clone().set(curve[0].x, curve[0].y, curve[0].z + distanceToFrame),
  );
  curve.unshift(
    curve[0].clone().set(curve[0].x, curve[0].y, curve[0].z + intrudingLength),
  );

  let lastPoint = curve[curve.length - 1].clone();
  curve.push(lastPoint.set(lastPoint.x - 0.05, lastPoint.y, lastPoint.z));

  const spline = new THREE.CatmullRomCurve3(curve);

  let tubeGeometry = new THREE.TubeGeometry(
    spline,
    32,
    tubeThickness,
    10,
    false,
  );
  tubeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(width / 2, height / 2, -width / 2),
  );

  return tubeGeometry;
}

export function buildBelt(startPoint, endPoint, tubeThickness) {
  let curve = [
    new THREE.Vector3(startPoint[0], startPoint[1], startPoint[2]),
    new THREE.Vector3(endPoint[0], endPoint[1], endPoint[2]),
  ];

  const spline = new THREE.CatmullRomCurve3(curve);

  let tubeGeometry = new THREE.TubeGeometry(
    spline,
    16,
    tubeThickness,
    10,
    false,
  );

  return tubeGeometry;
}

export function buildElbowPipe(
  startPoint,
  cornerPoint,
  endPoint,
  tubeThickness
) {
  const start = new THREE.Vector3(...startPoint);
  const corner = new THREE.Vector3(...cornerPoint);
  const end = new THREE.Vector3(...endPoint);

  // Directions
  const dir1 = corner.clone().sub(start).normalize();
  const dir2 = end.clone().sub(corner).normalize();

  // How far the curve starts before the corner
  const bendRadius =
    Math.min(
      start.distanceTo(corner),
      corner.distanceTo(end)
    ) * 0.25;

  // Start/end points of the rounded section
  const p1 = corner.clone().sub(
    dir1.clone().multiplyScalar(bendRadius)
  );

  const p2 = corner.clone().add(
    dir2.clone().multiplyScalar(bendRadius)
  );

  // Straight -> rounded elbow -> straight
  const curve = new THREE.CatmullRomCurve3(
    [
      start,
      p1,
      corner,
      p2,
      end
    ],
    false,
    "centripetal"
  );

  const geometry = new THREE.TubeGeometry(
    curve,
    64,
    tubeThickness / 2,
    16,
    false
  );

  return geometry;
}

export function buildCurvedDuct(
  xRadius,
  yRadius,
  width,
  height,
  depth,
  thickness,
) {
  let extendDown = height - yRadius;
  let extentLeft = width - xRadius;
  let shape = createBentDuctShape(xRadius, yRadius, width, height, 0);

  let extrudeSettings = {
    depth: depth,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };
  let extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  extrudeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(
      -width / 2 + extentLeft,
      -height / 2 + extendDown,
      -depth / 2,
    ),
  );
  extrudeGeometry.applyMatrix4(new THREE.Matrix4().makeRotationY(Math.PI / 2));
  extrudeGeometry.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI));

  if (thickness) {
    let mesh = new THREE.Mesh(extrudeGeometry);

    xRadius -= thickness;
    yRadius -= thickness;
    width -= thickness;
    height -= thickness;
    depth -= thickness;
    shape = createBentDuctShape(xRadius, yRadius, width, height, width * 2);

    extrudeSettings = {
      depth: depth,
      steps: 1,
      bevelEnabled: false,
      curveSegments: 16,
    };
    extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    extrudeGeometry.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        -width / 2 + extentLeft,
        -height / 2 + extendDown,
        -depth / 2,
      ),
    );
    extrudeGeometry.applyMatrix4(
      new THREE.Matrix4().makeRotationY(Math.PI / 2),
    );
    extrudeGeometry.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI));

    let subtractMesh = new THREE.Mesh(extrudeGeometry);

    let newMesh = CSG.subtract(mesh, subtractMesh);
    return newMesh.geometry;
  }

  return extrudeGeometry;
}

export const buildDuct = (
  height,
  depth,
  radial,
  holeWidth,
  holeHeight,
  holeRadius,
  distanceFromTop,
  thickness = 0.01,
) => {
  const shape = new THREE.Shape();
  const holePath = new THREE.Path();
  const positionY =
    height / 2 - (radial ? holeRadius : holeHeight / 2) - distanceFromTop;

  if (radial) {
    shape.absarc(0, positionY, holeRadius, 0, Math.PI * 2, 0, false);
    holePath.absarc(0, positionY, holeRadius - thickness, 0, Math.PI * 2, true);
  } else {
    const ductWidth = holeWidth;
    const ductHeight = holeHeight;

    const offsetWidth = ductWidth / 2;
    const offsetHeight = -height / 2 + ductHeight + distanceFromTop;

    shape
      .moveTo(0 - offsetWidth, 0 - offsetHeight)
      .lineTo(0 - offsetWidth, ductHeight - offsetHeight)
      .lineTo(ductWidth - offsetWidth, ductHeight - offsetHeight)
      .lineTo(ductWidth - offsetWidth, 0 - offsetHeight)
      .lineTo(0 - offsetWidth, 0 - offsetHeight);

    holePath
      .moveTo(thickness - offsetWidth, thickness - offsetHeight)
      .lineTo(thickness - offsetWidth, ductHeight - thickness - offsetHeight)
      .lineTo(
        ductWidth - offsetWidth - thickness,
        ductHeight - thickness - offsetHeight,
      )
      .lineTo(ductWidth - offsetWidth - thickness, thickness - offsetHeight)
      .lineTo(thickness - offsetWidth, thickness - offsetHeight);
  }

  shape.holes.push(holePath);

  return shape;
};

export const buildCylindricalFilterPlate = (
  width,
  height,
  length,
  holeDiameter,
) => {
  let plate = new THREE.Mesh(new THREE.BoxGeometry(length, height, width));
  let subtractMesh = new THREE.Mesh(
    new THREE.BoxGeometry(length, height - fromMM(5), width - fromMM(5)),
  );
  subtractMesh.position.set(-fromMM(5), 0, 0);
  plate = CSG.subtract(plate, subtractMesh);

  subtractMesh = new THREE.Mesh(
    new THREE.CylinderGeometry(
      holeDiameter / 2,
      holeDiameter / 2,
      length * 2,
      8,
      8,
    ),
  );
  subtractMesh.rotation.set(0, 0, Math.PI / 2);

  const startX = align(0, width, width / 4, COMPONENT_ALIGNMENT.FRONT);
  const startY = align(0, height, height / 4, COMPONENT_ALIGNMENT.TOP);

  for (let x = 0; x < 4; x++)
    for (let y = 0; y < 4; y++) {
      subtractMesh.position.set(
        0,
        startY - y * (width / 4),
        startX - x * (width / 4),
      );
      plate = CSG.subtract(plate, subtractMesh);
    }

  return plate.geometry;
};

export const buildDuctPlateWithHole = (
  height,
  depth,
  radial,
  holeWidth,
  holeHeight,
  holeRadius,
  distanceFromTop,
  thickness = 0.01,
) => {
  const box = new THREE.Mesh(new THREE.BoxGeometry(depth, height, thickness));
  const positionY =
    height / 2 - (radial ? holeRadius : holeHeight / 2) - distanceFromTop;

  var subtractMesh = null;

  if (radial) {
    subtractMesh = new THREE.Mesh(
      new THREE.CylinderGeometry(holeRadius, holeRadius, 2, 16, 10),
    );
    subtractMesh.rotation.x = Math.PI / 2;
    subtractMesh.updateMatrix();
  } else {
    subtractMesh = new THREE.Mesh(
      new THREE.BoxGeometry(holeWidth, holeHeight, 10),
    );
  }

  subtractMesh.position.y = positionY;

  const newMesh = CSG.subtract(box, subtractMesh);
  return newMesh.geometry;
};

const buildCentrifugalShape = (
  width,
  height,
  outletHeight,
  outletLength,
  extendOutlet = 0,
) => {
  const shape = new THREE.Shape();

  const h = height;
  const w = width;
  const o = outletLength; //w * 0.125; // ammount off width to allow for outlet frame (12.5%)

  const startingPoint = [w * 0.2, h / 2]; // start slightly to the right (20%)
  const b1 = [0, h / 2, (-w / 2) * 0.875, h / 2, -w / 2, 0]; // some factors applied to tangents (87.5%)
  const b2 = [-w / 2, -h / 2, 0, -h / 2, 0, -h / 2];
  const b3 = [
    0,
    -h / 2,
    (w / 2 - o) * 0.94,
    -h / 2,
    w / 2 - o,
    -outletHeight + h / 2,
  ]; // some factors applied to tangents (94%)
  const l1 = [extendOutlet + w / 2, -outletHeight + h / 2];
  const l2 = [extendOutlet + w / 2, h / 2];

  shape.moveTo(l2[0], l2[1]);
  shape.lineTo(startingPoint[0], startingPoint[1]);
  shape.bezierCurveTo(b1[0], b1[1], b1[2], b1[3], b1[4], b1[5]);
  shape.bezierCurveTo(b2[0], b2[1], b2[2], b2[3], b2[4], b2[5]);
  shape.bezierCurveTo(b3[0], b3[1], b3[2], b3[3], b3[4], b3[5]);
  shape.lineTo(l1[0], l1[1]);
  //shape.lineTo(l2[0], l2[1]);

  var holeRadius = h * 0.25;
  var holeYPosition = -(holeRadius / 2);
  var holePath = new THREE.Path();
  holePath.absarc(0, holeYPosition, holeRadius, 0, Math.PI * 2, true);
  shape.holes.push(holePath);

  return shape;
};

export const buildCentrifugal = (
  width,
  height,
  fanWidth,
  outletHeight,
  outletLength,
  shaftSide,
) => {
  const shape = buildCentrifugalShape(
    width,
    height,
    outletHeight,
    outletLength,
  );

  let extrudeSettings = {
    depth: fanWidth,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };
  let extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  extrudeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, 0, -fanWidth / 2),
  );

  const innerAmmount = 0.05;
  const innerFactor = 1 - innerAmmount;
  const innerShape = buildCentrifugalShape(
    width * innerFactor,
    height * innerFactor,
    outletHeight * innerFactor * (innerFactor + innerAmmount / 2),
    outletLength,
    0.5,
  );
  extrudeSettings = {
    depth: fanWidth * innerFactor,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };

  let innerExtrudeGeometry = new THREE.ExtrudeGeometry(
    innerShape,
    extrudeSettings,
  );
  innerExtrudeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, 0, (-fanWidth * innerFactor) / 2),
  );

  let mesh = new THREE.Mesh(extrudeGeometry);
  let subtractMesh = new THREE.Mesh(innerExtrudeGeometry);
  let newMesh = CSG.subtract(mesh, subtractMesh);

  // hole for inlet

  // let cylinder = new THREE.CylinderBufferGeometry(0.12, 0.12, depth, 16);
  // cylinder.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  // cylinder.applyMatrix4(new THREE.Matrix4().makeTranslation(0, -0.04, 0));
  // subtractMesh = new THREE.Mesh(cylinder);
  // mesh = CSG.subtract(newMesh, subtractMesh);

  var holeRadius = height * 0.25;
  var holeYPosition = -(holeRadius / 2);

  const inletWidth = 0.01;

  let inlet = new THREE.CylinderGeometry(
    holeRadius,
    holeRadius * 0.8,
    inletWidth,
    32,
    undefined,
    true,
  );
  inlet.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  inlet.applyMatrix4(
    new THREE.Matrix4().makeTranslation(
      0,
      holeYPosition,
      fanWidth / 2 - inletWidth / 2,
    ),
  );

  let inlet2 = new THREE.CylinderGeometry(
    holeRadius,
    holeRadius * 0.8,
    inletWidth,
    32,
    undefined,
    true,
  );
  inlet2.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  inlet2.applyMatrix4(new THREE.Matrix4().makeRotationY(Math.PI));
  inlet2.applyMatrix4(
    new THREE.Matrix4().makeTranslation(
      0,
      holeYPosition,
      -fanWidth / 2 + inletWidth / 2,
    ),
  );

  let impeller = new THREE.CylinderGeometry(
    holeRadius * 0.8,
    holeRadius * 0.8,
    fanWidth - inletWidth * 2,
    32,
    undefined,
    true,
  );
  impeller.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  impeller.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, holeYPosition, 0),
  );

  let shaft = new THREE.CylinderGeometry(0.01, 0.01, fanWidth, 32);
  shaft.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  shaft.applyMatrix4(
    new THREE.Matrix4().makeTranslation(
      0,
      holeYPosition,
      fromMM(180) * shaftSide,
    ),
  );

  const shaft_bearing_width = 0.05;
  let shaft_bearing = new THREE.CylinderGeometry(
    holeRadius * 0.4,
    holeRadius * 0.4,
    shaft_bearing_width,
    32,
  );
  shaft_bearing.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
  shaft_bearing.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, holeYPosition, 0),
  );

  return [
    newMesh.geometry,
    inlet,
    inlet2,
    impeller,
    shaft_bearing,
    shaft,
  ];
};

export const buildESPConeGeometry = (sections, hasLeftCone) => {
  const geom = [];
  for (let s = 0; s < sections.length; s++) {
    const section = sections[s];
    let coneSection = buildESPConeGeometrySection(
      section[0],
      section[1],
      section[2],
      section[3],
      section[4],
      section[5],
    );
    if (hasLeftCone) {
      geom.push(coneSection);
    }

    section[0].x *= -1;
    section[3].x *= -1;
    coneSection = buildESPConeGeometrySection(
      section[0],
      section[1],
      section[2],
      section[3],
      section[4],
      section[5],
    );
    geom.push(coneSection);
  }

  return mergeBufferGeometries(geom);
};

const buildESPConeGeometrySection = (point1, w1, h1, point2, w2, h2) => {
  const vertices = [
    point1,
    new THREE.Vector3(point1.x, point1.y + h1, point1.z + w1),
    new THREE.Vector3(point2.x, point2.y + h2, point2.z + w2),
    new THREE.Vector3(point2.x, point2.y, point2.z),
  ];

  const geometry = new ConvexGeometry(vertices);
  return geometry;
};

export const buildExtrudedBoxGeometry = (width, height, depth) => {
  const shape = new THREE.Shape();
  let extrudeSettings = {
    depth: width,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };

  const offsetWidth = depth / 2;
  const offsetHeight = height / 2;

  shape
    .moveTo(0 - offsetWidth, 0 - offsetHeight)
    .lineTo(0 - offsetWidth, height - offsetHeight)
    .lineTo(depth - offsetWidth, height - offsetHeight)
    .lineTo(depth - offsetWidth, 0 - offsetHeight)
    .lineTo(0 - offsetWidth, 0 - offsetHeight);

  let extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  extrudeGeometry.applyMatrix4(new THREE.Matrix4().makeRotationY(Math.PI / 2));
  extrudeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(-width / 2, 0, 0),
  );

  return extrudeGeometry;
};

export function buildFrameGeometry(
  length,
  width,
  height,
  hasLeftFrame,
  hasRightFrame,
  hasFloor,
  hasLeftWall,
  hasRightWall,
  hasFrame,
  frameThickness,
  rows = [1],
) {
  const geos = [];

  if (hasFrame === false) {
    return mergeBufferGeometries([new THREE.BoxGeometry(0, 0, 0)]);
  }

  const frame_length = frameThickness || DEFAULT_EMPTY_THICKNESS;

  const right_offset = length / 2 + frame_length / 2;
  const left_offset = -1 * right_offset;

  const front_offset = width / 2 + frame_length / 2;
  const back_offset = -1 * front_offset;

  const top_offset = height / 2 + frame_length / 2;
  const bottom_offset = -1 * top_offset;

  const sizeFactor = 0.999;

  let y =
    align(0, height, rows[0] * height, COMPONENT_ALIGNMENT.BOTTOM) -
    (rows[0] * height) / 2;

  for (let i = 0; i < rows.length; i++) {
    const trans_y = y + (rows[i] * height) / 2;

    if (hasLeftFrame) {
      const back_left = new THREE.BoxGeometry(
        frame_length,
        height * rows[i],
        frame_length,
      ); // new THREE.BoxGeometry(frame_length, height, frame_length);
      back_left.applyMatrix4(
        new THREE.Matrix4().makeTranslation(left_offset, trans_y, back_offset),
      );
      geos.push(back_left);
    }

    if (hasRightFrame) {
      const back_right = new THREE.BoxGeometry(
        frame_length,
        height * rows[i],
        frame_length,
      );
      back_right.applyMatrix4(
        new THREE.Matrix4().makeTranslation(right_offset, trans_y, back_offset),
      );
      geos.push(back_right);
    }

    if (hasLeftFrame) {
      const front_left = new THREE.BoxGeometry(
        frame_length,
        height * rows[i],
        frame_length,
      );
      front_left.applyMatrix4(
        new THREE.Matrix4().makeTranslation(left_offset, trans_y, front_offset),
      );
      geos.push(front_left);
    }
    if (hasRightFrame) {
      const front_right = new THREE.BoxGeometry(
        frame_length,
        height * rows[i],
        frame_length,
      );
      front_right.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          right_offset,
          trans_y,
          front_offset,
        ),
      );
      geos.push(front_right);
    }

    if (i < rows.length - 1) {
      if (hasLeftFrame) {
        const left_top = new THREE.BoxGeometry(
          frame_length,
          frame_length,
          width * sizeFactor,
        );
        left_top.applyMatrix4(
          new THREE.Matrix4().makeTranslation(
            left_offset,
            trans_y + (height * rows[i]) / 2,
            0,
          ),
        );
        geos.push(left_top);
      }

      if (hasRightFrame) {
        const right_top = new THREE.BoxGeometry(
          frame_length,
          frame_length,
          width * sizeFactor,
        );
        right_top.applyMatrix4(
          new THREE.Matrix4().makeTranslation(
            right_offset,
            trans_y + (height * rows[i]) / 2,
            0,
          ),
        );
        geos.push(right_top);
      }

      const back_top = new THREE.BoxGeometry(
        length * sizeFactor,
        frame_length,
        frame_length,
      );
      back_top.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          0,
          trans_y + (height * rows[i]) / 2,
          back_offset,
        ),
      );
      geos.push(back_top);

      const front_top = new THREE.BoxGeometry(
        length * sizeFactor,
        frame_length,
        frame_length,
      );
      front_top.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          0,
          trans_y + (height * rows[i]) / 2,
          front_offset,
        ),
      );
      geos.push(front_top);
    }

    y += Math.abs(rows[i]) * height;
  }

  const back_top = new THREE.BoxGeometry(
    length * sizeFactor,
    frame_length,
    frame_length,
  );
  back_top.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, top_offset, back_offset),
  );
  geos.push(back_top);

  const back_bottom = new THREE.BoxGeometry(
    length * sizeFactor,
    frame_length,
    frame_length,
  );
  back_bottom.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, bottom_offset, back_offset),
  );
  geos.push(back_bottom);

  const front_top = new THREE.BoxGeometry(
    length * sizeFactor,
    frame_length,
    frame_length,
  );
  front_top.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, top_offset, front_offset),
  );
  geos.push(front_top);

  const front_bottom = new THREE.BoxGeometry(
    length * sizeFactor,
    frame_length,
    frame_length,
  );
  front_bottom.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, bottom_offset, front_offset),
  );
  geos.push(front_bottom);

  if (hasLeftFrame) {
    const left_top = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      width * sizeFactor,
    );
    left_top.applyMatrix4(
      new THREE.Matrix4().makeTranslation(left_offset, top_offset, 0),
    );
    geos.push(left_top);

    const left_bottom = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      width * sizeFactor,
    );
    left_bottom.applyMatrix4(
      new THREE.Matrix4().makeTranslation(left_offset, bottom_offset, 0),
    );
    geos.push(left_bottom);
  }

  if (hasRightFrame) {
    const right_top = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      width * sizeFactor,
    );
    right_top.applyMatrix4(
      new THREE.Matrix4().makeTranslation(right_offset, top_offset, 0),
    );
    geos.push(right_top);

    const right_bottom = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      width * sizeFactor,
    );
    right_bottom.applyMatrix4(
      new THREE.Matrix4().makeTranslation(right_offset, bottom_offset, 0),
    );
    geos.push(right_bottom);
  }

  const panel_thickness = fromMM(5);
  const panel_length =
    hasLeftFrame && hasRightFrame
      ? length
      : hasLeftFrame || hasRightFrame
        ? length
        : length;
  const panel_height = height;
  const panel_length_offset = 0; //hasLeftFrame && hasRightFrame ? 0 : hasLeftFrame ? frameThickness / 2 : hasRightFrame ? -frameThickness / 2 : 0;
  const panel_height_offset = panel_thickness / 2 + height / 2; // - frame_length;

  // if (hasFloor) {
  //   const floor = new THREE.BoxGeometry(panel_length * sizeFactor, panel_thickness, width * sizeFactor);
  //   floor.applyMatrix4(new THREE.Matrix4().makeTranslation(panel_length_offset, panel_height_offset * -1, 0));
  //   geos.push(floor);
  // }

  // if (hasCeiling) {
  //   const ceiling = new THREE.BoxGeometry(panel_length, panel_thickness, width);
  //   ceiling.applyMatrix4(new THREE.Matrix4().makeTranslation(panel_length_offset, panel_height_offset - panel_thickness + frame_length, 0));
  //   geos.push(ceiling);
  // }

  // if (hasLeftWall) {
  //   const wall = new THREE.BoxGeometry(panel_thickness, panel_height, width);
  //   wall.applyMatrix4(new THREE.Matrix4().makeTranslation(-(frame_length + (length - panel_thickness) / 2), 0, 0));
  //   geos.push(wall);
  // }

  // if (hasRightWall) {
  //   const wall = new THREE.BoxGeometry(panel_thickness, panel_height, width);
  //   wall.applyMatrix4(new THREE.Matrix4().makeTranslation(frame_length + (length - panel_thickness) / 2, 0, 0));
  //   geos.push(wall);
  // }
  return mergeBufferGeometries(geos);
}

export function buildCornersGeometry(
  length,
  width,
  height,
  hasLeftFrame,
  hasRightFrame,
  frameThickness,
  rows = [1],
) {
  const geos = [];

  if (hasLeftFrame == false && hasRightFrame == false) return null;

  let frame_length = (frameThickness || DEFAULT_EMPTY_THICKNESS) * 1;

  const right_offset = length / 2 + frame_length / 2;
  const left_offset = -1 * right_offset;

  const front_offset = width / 2 + frame_length / 2;
  const back_offset = -1 * front_offset;

  const top_offset = height / 2 + frame_length / 2;
  const bottom_offset = -1 * top_offset;

  frame_length *= 1.01;

  let y =
    align(0, height, rows[0] * height, COMPONENT_ALIGNMENT.BOTTOM) -
    (rows[0] * height) / 2;

  for (let i = 0; i < rows.length - 1; i++) {
    const trans_y = y + (rows[i] * height) / 2;

    if (hasLeftFrame) {
      // Left
      const back_top = new THREE.BoxGeometry(
        frame_length,
        frame_length,
        frame_length,
      );
      back_top.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          left_offset,
          trans_y + (height * rows[i]) / 2,
          back_offset,
        ),
      );
      geos.push(back_top);

      const front_top = new THREE.BoxGeometry(
        frame_length,
        frame_length,
        frame_length,
      );
      front_top.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          left_offset,
          trans_y + (height * rows[i]) / 2,
          front_offset,
        ),
      );
      geos.push(front_top);
    }

    if (hasRightFrame) {
      // Right
      const left_top = new THREE.BoxGeometry(
        frame_length,
        frame_length,
        frame_length,
      );
      left_top.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          right_offset,
          trans_y + (height * rows[i]) / 2,
          back_offset,
        ),
      );
      geos.push(left_top);

      const right_top = new THREE.BoxGeometry(
        frame_length,
        frame_length,
        frame_length,
      );
      right_top.applyMatrix4(
        new THREE.Matrix4().makeTranslation(
          right_offset,
          trans_y + (height * rows[i]) / 2,
          front_offset,
        ),
      );
      geos.push(right_top);
    }

    y += Math.abs(rows[i]) * height;
  }

  if (hasLeftFrame) {
    // Left
    const back_top = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    back_top.applyMatrix4(
      new THREE.Matrix4().makeTranslation(left_offset, top_offset, back_offset),
    );
    geos.push(back_top);

    const back_bottom = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    back_bottom.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        left_offset,
        bottom_offset,
        back_offset,
      ),
    );
    geos.push(back_bottom);

    const front_top = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    front_top.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        left_offset,
        top_offset,
        front_offset,
      ),
    );
    geos.push(front_top);

    const front_bottom = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    front_bottom.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        left_offset,
        bottom_offset,
        front_offset,
      ),
    );
    geos.push(front_bottom);
  }

  if (hasRightFrame) {
    // Right
    const left_top = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    left_top.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        right_offset,
        top_offset,
        back_offset,
      ),
    );
    geos.push(left_top);

    const left_bottom = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    left_bottom.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        right_offset,
        bottom_offset,
        back_offset,
      ),
    );
    geos.push(left_bottom);

    const right_top = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    right_top.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        right_offset,
        top_offset,
        front_offset,
      ),
    );
    geos.push(right_top);

    const right_bottom = new THREE.BoxGeometry(
      frame_length,
      frame_length,
      frame_length,
    );
    right_bottom.applyMatrix4(
      new THREE.Matrix4().makeTranslation(
        right_offset,
        bottom_offset,
        front_offset,
      ),
    );
    geos.push(right_bottom);
  }

  return mergeBufferGeometries(geos);
}

export function buildBagFilter(height, length, thickness, ammount) {
  const geometry = new THREE.BufferGeometry();

  const reducedEnd = (height / 2) * 0.6;
  const reducedStart = (height / 2) * 0.9;
  const end = length / 2;
  const start = -length / 2;
  const height_start = height / 2;
  const height_end = -height / 2;

  const vertices = new Float32Array([
    end,
    0,
    reducedEnd,
    //
    start,
    thickness,
    height_start,
    //
    start,
    0,
    reducedStart,
    //
    start,
    thickness,
    height_end,
    //
    end,
    0,
    -reducedEnd,
    //
    start,
    0,
    -reducedStart,
    //
    end,
    0,
    -reducedEnd,
    //
    end,
    0,
    reducedEnd,
    //
    end,
    0,
    reducedEnd,
    //
    end,
    0,
    -reducedEnd,
    //
    end,
    0,
    reducedEnd,
    //
    start,
    thickness,
    height_end,
    //
    start,
    thickness,
    height_start,
    //
    end,
    0,
    -reducedEnd,
    //
    start,
    thickness,
    height_start,
    //
    start,
    0,
    -reducedStart,
    //
    start,
    0,
    reducedStart,
    //
    start,
    thickness,
    height_end,
  ]);

  const normals = new Float32Array([
    0, -0.904006, 0.427466, 0, -0.904006, 0.427466, 0, -0.904006, 0.427466, 0,
    -0.908168, -0.418585, 0, -0.908168, -0.418585, 0.00420923, -0.908168,
    -0.418585, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0.10544, 1, 0, 0.10544, 1, 0,
    0.10544, 1, 0, 0.10544, 1, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0,
  ]);

  const indices = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 8, 9, 6, 10, 11, 12, 11, 10, 13, 14, 15, 16, 14,
    17, 15,
  ];

  geometry.setIndex(indices);
  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));

  let geometry_clone = geometry.clone();
  geometry_clone.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI));
  geometry_clone.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0, 0));

  let complete_geometry = mergeBufferGeometries([geometry, geometry_clone]);

  // // Make the filters open
  // // Not sure if we're bothered about this or not
  // let mesh = new THREE.Mesh(complete_geometry);
  // let mesh_smaller = new THREE.Mesh(complete_geometry.clone());
  // mesh_smaller.scale.set(0.8, 0.95, 0.8);
  // mesh_smaller.position.set(-0.05, 0, 0);
  // let result_mesh = CSG.subtract(mesh, mesh_smaller);
  // complete_geometry = result_mesh.geometry;
  // //

  let geos = [complete_geometry];

  let offset = thickness * 2;
  for (let i = 1; i < ammount; i++) {
    let geometry_clone = complete_geometry.clone();
    geometry_clone.applyMatrix4(
      new THREE.Matrix4().makeTranslation(0, 0, offset),
    );
    geos.push(geometry_clone);
    offset += thickness * 2;
  }

  complete_geometry = mergeBufferGeometries(geos);
  complete_geometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(0, 0, thickness + -ammount * thickness),
  );

  return complete_geometry;
}

export function buildPanel(length, height, width, louvered) {
  let geometry = new THREE.BoxGeometry(length, height, width);

  if (louvered) {
    const louvers = [];

    const louverLength = fromMM(150);
    const louverHeight = width;
    const louverWidth = width;
    const louverGeometry = new THREE.BoxGeometry(
      louverLength,
      louverHeight,
      louverWidth,
    );
    louverGeometry.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 4));

    const numberOfRows = Math.floor(height / (louverHeight * 6));
    const numberOfColumns = Math.floor(length / (louverLength * 1.5));

    const distance = height / numberOfRows;
    const columnDistance = length / numberOfColumns;

    for (let i = 0; i < numberOfRows; i++) {
      for (let j = 0; j < numberOfColumns; j++) {
        const clonedLouver = louverGeometry.clone();
        clonedLouver.applyMatrix4(
          new THREE.Matrix4().makeTranslation(
            columnDistance / 2 + j * columnDistance - length / 2,
            distance / 2 + i * distance - height / 2,
            louverWidth / 2,
          ),
        );
        louvers.push(clonedLouver);
      }
    }

    let mergedGeometry = mergeBufferGeometries(louvers);
    let louverMesh = new THREE.Mesh(mergedGeometry);
    let panelMesh = new THREE.Mesh(geometry);
    let result = CSG.subtract(louverMesh, panelMesh);

    return mergeBufferGeometries([geometry, result.geometry]);
  }
  return geometry;
}

export function buildEspPipe(
  width,
  height,
  length,
  tubeThickness,
  distanceToFrame,
  intrudingLength,
  downwardSlope = 0,
) {
  width -= tubeThickness * 2;
  //height -= tubeThickness * 2;

  const roundedHeight = 0.05;

  function ccc(t) {
    const t2 = ((2 * Math.PI) / 2) * t * 0.5;
    const x = -Math.cos(t2);
    const z = -Math.sin(t2);

    const y = t;
    return new THREE.Vector3(x * width, 0, z * (length / 2));
    // return new THREE.Vector3(-x * width, 0, 0);
  }

  let curve = [];
  let numberOfPoints = 30;

  for (let i = 0; i <= numberOfPoints; i++) {
    curve.push(ccc(i / numberOfPoints));
  }

  const uplift = 0.0;
  const gap = width / 2;

  //console.log("curve", curve);

  curve.unshift(
    curve[0].clone().set(curve[0].x, curve[0].y, curve[0].z + distanceToFrame),
  );
  let lastPoint = curve[curve.length - 1].clone();
  curve.push(lastPoint.set(lastPoint.x + 0.01, lastPoint.y, lastPoint.z));

 // console.log("finished - curve", curve);

  const spline = new THREE.CatmullRomCurve3(curve);

  let tubeGeometry = new THREE.TubeGeometry(
    spline,
    32,
    tubeThickness,
    10,
    false,
  );
  tubeGeometry.applyMatrix4(
    new THREE.Matrix4().makeTranslation(width / 2, height / 2, 0),
  );

  return tubeGeometry;
}

/**
 * BOX-T-HT base frame: the channel that wraps the fan's mounting bars.
 *
 * In the client drawing this is not a slab sitting on top of the bars - it is
 * a perimeter band standing AROUND them, so from the side you see the frame
 * band with the bar hidden inside it.
 *
 * The floor ring carries its own outline: both pairs of ends are pulled IN,
 * each by its own amount, and the transition is taken up over `taperHeight`.
 * So the SIDE elevation shows slanted corners at the X ends and the FRONT
 * elevation shows slanted corners at the Z ends - the angled bottom corners
 * marked on the client drawing: the foot is wider at the top, under the box,
 * and drawn in at the floor. Plan corners are left square.
 *
 * Built as a loft: the same rectangular outline at two or three heights with
 * the walls joining them, capped top and bottom. Centred on the origin with its
 * TOP face at y = 0, so the caller positions it by where the box above sits.
 *
 * Solid rather than hollow: the cavity is never visible in 3D, while its inner
 * wall projected a second chamfer line inside each end of the band, and the
 * drawing shows the band's outline only.
 *
 *   length       footprint along X (the fan's length)
 *   width        footprint along Z (across the fan, key B)
 *   height       how far the channel hangs down from the box underside
 *   footInset    how far each END is drawn in at the floor (along X)
 *   taperHeight  height over which the insets are taken up
 *   footInsetZ   how far each END is drawn in at the floor (along Z)
 */
export const buildBaseFrameChannel = (
  length,
  width,
  height,
  footInset = 0,
  taperHeight = 0,
  footInsetZ = 0,
) => {
  // Rectangular outline as XZ points. `inset` pulls the two X ends in and
  // `insetZ` the two Z ends; both apply to the floor ring only.
  const outline = (inset, insetZ = 0) => {
    const x = Math.max(length / 2 - inset, 0);
    const z = Math.max(width / 2 - insetZ, 0);
    return [
      [-x, -z],
      [x, -z],
      [x, z],
      [-x, z],
    ];
  };

  // Clamp the taper so it can never exceed the frame or close the footprint.
  const inset = Math.max(0, Math.min(footInset, length / 2));
  const insetZ = Math.max(0, Math.min(footInsetZ, width / 2));
  const taper = Math.max(0, Math.min(taperHeight, height));

  // Ring levels, from the floor up to the box underside. With no taper this
  // collapses to a plain vertical extrusion. A level that lands on the same
  // height as the one below it is dropped, so a full-height taper does not
  // leave a degenerate zero-height band behind.
  const ringLevels =
    (inset > 0 || insetZ > 0) && taper > 0
      ? [
          { y: -height, pts: outline(inset, insetZ) },
          { y: -height + taper, pts: outline(0, 0) },
          { y: 0, pts: outline(0, 0) },
        ]
      : [
          { y: -height, pts: outline(0, 0) },
          { y: 0, pts: outline(0, 0) },
        ];
  const levels = ringLevels.filter(
    (lvl, i) => i === 0 || lvl.y - ringLevels[i - 1].y > 1e-6,
  );

  const positions = [];
  const pushTri = (a, b, c) => positions.push(...a, ...b, ...c);
  const at = (lvl, i) => [lvl.pts[i][0], lvl.y, lvl.pts[i][1]];

  const n = levels[0].pts.length;

  // Side walls, level to level. Only the outer skin exists, so the chamfered
  // ends draw as one line each rather than a wall thickness apart.
  for (let l = 0; l < levels.length - 1; l++) {
    const lo = levels[l];
    const hi = levels[l + 1];
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      pushTri(at(lo, i), at(hi, i), at(hi, j));
      pushTri(at(lo, i), at(hi, j), at(lo, j));
    }
  }

  // Flat caps over the whole footprint, triangulated as a fan.
  const cap = (lvl, flip) => {
    for (let i = 1; i < n - 1; i++) {
      const a = at(lvl, 0);
      const b = at(lvl, i);
      const c = at(lvl, i + 1);
      if (flip) pushTri(a, c, b);
      else pushTri(a, b, c);
    }
  };
  cap(levels[levels.length - 1], true); // top face, +Y
  cap(levels[0], false); // floor face, -Y

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.computeVertexNormals();

  return geometry;
};
